import { productImageAlt } from "@/lib/imageAlt";
import { getShopifyImageUrl } from "@/lib/shopify";
import { sameImageUrl } from "@/lib/variants";
// One src/srcset/sizes for both heroes below and the prerendered head preload.
import { pdpImageSources } from "../../../scripts/pdp-image.mjs";
import { useState, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";

const useImageZoom = () => {
  const [zoomPos, setZoomPos] = useState<{ x: number; y: number } | null>(null);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x, y });
  };
  const handleMouseLeave = () => setZoomPos(null);
  return { zoomPos, handleMouseMove, handleMouseLeave };
};

interface ImageEdge {
  node: { url: string; altText: string | null };
}

interface ImageGalleryProps {
  images: ImageEdge[];
  title: string;
  /** The selected variant's own photo; the gallery moves to it when it changes. */
  activeImageUrl?: string | null;
}

export const ImageGallery = ({ images, title, activeImageUrl }: ImageGalleryProps) => {
  const activeIndex = activeImageUrl ? images.findIndex((i) => sameImageUrl(i.node.url, activeImageUrl)) : -1;
  // Starts on the variant photo, so a page that opens on "Bronze" never flashes
  // the "Silver" lead image first.
  const [selectedImage, setSelectedImage] = useState(Math.max(0, activeIndex));
  // Picking a colour or size shows that variant's photo, not only its price.
  // Set while rendering, not in an effect: the list gains or loses a photo in
  // this same render, and an effect would first paint, and start downloading,
  // whatever image now sits at the old position.
  const [shownFor, setShownFor] = useState(activeImageUrl);
  if (shownFor !== activeImageUrl) {
    setShownFor(activeImageUrl);
    // The list is one shorter again when the next variant has no photo of its own.
    setSelectedImage(activeIndex >= 0 ? activeIndex : selectedImage < images.length ? selectedImage : 0);
  }
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStart = useRef({ x: 0, y: 0 });
  const swiped = useRef(false);
  const { zoomPos, handleMouseMove, handleMouseLeave } = useImageZoom();

  const hero = images[selectedImage] ? pdpImageSources(images[selectedImage].node.url, getShopifyImageUrl) : null;

  const prev = () => setSelectedImage(p => (p > 0 ? p - 1 : images.length - 1));
  const next = () => setSelectedImage(p => (p < images.length - 1 ? p + 1 : 0));

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") prev();
    if (e.key === "ArrowRight") next();
    if (e.key === "Escape") setLightboxOpen(false);
  }, [images.length]);

  return (
    <div>
      {/* Desktop: Large hero + thumbnail strip */}
      <div className="hidden md:block">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="aspect-[4/3] overflow-hidden rounded-2xl bg-sand relative group/hero cursor-zoom-in"
          onClick={() => setLightboxOpen(true)}
          onKeyDown={handleKeyDown}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          tabIndex={0}
        >
          {hero ? (
            <img
              src={hero.src}
              srcSet={hero.srcset}
              sizes={hero.sizes}
              alt={productImageAlt(title, { displayName: title, index: selectedImage, total: images.length })}
              className="w-full h-full object-contain mix-blend-multiply transition-transform duration-500"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              style={zoomPos ? { transform: 'scale(2)', transformOrigin: `${zoomPos.x}% ${zoomPos.y}%` } : undefined}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">No Image</div>
          )}
          {images.length > 1 && (
            <div className="absolute top-1/2 -translate-y-1/2 left-3 right-3 flex justify-between pointer-events-none opacity-0 group-hover/hero:opacity-100 transition-opacity duration-200">
              <button aria-label="Previous image" onClick={(e) => { e.stopPropagation(); prev(); }} className="pointer-events-auto w-10 h-10 rounded-full bg-background shadow flex items-center justify-center hover:bg-background transition-colors">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button aria-label="Next image" onClick={(e) => { e.stopPropagation(); next(); }} className="pointer-events-auto w-10 h-10 rounded-full bg-background shadow flex items-center justify-center hover:bg-background transition-colors">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
          <span className="absolute left-1/2 -translate-x-1/2 bottom-5 bg-background border border-border rounded-full px-5 py-2.5 flex items-center gap-2 text-[13px] font-medium text-foreground shadow-sm pointer-events-none opacity-0 group-hover/hero:opacity-100 transition-opacity duration-200">
            <Maximize2 className="h-4 w-4" />
            Click to enlarge
          </span>
        </motion.div>
        {images.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="flex gap-2 mt-3 overflow-x-auto scrollbar-hide pb-1"
          >
            {/* Every image: a variant photo put first makes eleven. */}
            {images.map((image, index) => (
              <button
                key={index}
                aria-label={`Show image ${index + 1} of ${images.length} — ${title}`}
                aria-pressed={index === selectedImage}
                onClick={() => setSelectedImage(index)}
                className={`flex-shrink-0 w-[72px] h-[72px] overflow-hidden bg-[#f5f0eb] transition-all ${
                  index === selectedImage ? "ring-2 ring-foreground ring-offset-1" : "opacity-60 hover:opacity-100"
                }`}
              >
                <img src={getShopifyImageUrl(image.node.url, 160)} alt={productImageAlt(title, { displayName: title, index, total: images.length })} className="w-full h-full object-contain mix-blend-multiply" loading="lazy" decoding="async" />
              </button>
            ))}
          </motion.div>
        )}
      </div>

      {/* Mobile: swipe-only + counter */}
      <div className="md:hidden">
        <div
          role="button"
          tabIndex={0}
          aria-label={`Enlarge product image — ${title}`}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setLightboxOpen(true); } }}
          className="aspect-[4/3] max-h-[55vh] md:max-h-none overflow-hidden bg-sand relative touch-pan-y"
          onTouchStart={(e) => {
            const touch = e.touches[0];
            if (!touch) return;
            swiped.current = false;
            touchStart.current = { x: touch.clientX, y: touch.clientY };
          }}
          onTouchEnd={(e) => {
            const touch = e.changedTouches[0];
            if (!touch) return;
            const diff = touchStart.current.x - touch.clientX;
            const vertical = touchStart.current.y - touch.clientY;
            swiped.current = Math.abs(diff) > 10 || Math.abs(vertical) > 10;
            if (images.length > 1 && Math.abs(diff) > 50 && Math.abs(diff) > Math.abs(vertical)) {
              diff > 0 ? next() : prev();
            }
          }}
          onClick={() => {
            if (swiped.current) { swiped.current = false; return; }
            setLightboxOpen(true);
          }}
        >
          {hero ? (
            <img src={hero.src} srcSet={hero.srcset} sizes={hero.sizes} alt={productImageAlt(title, { displayName: title, index: selectedImage, total: images.length })} className="w-full h-full object-contain mix-blend-multiply" loading="eager" fetchPriority="high" decoding="async" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">No Image</div>
          )}
        </div>
        {images.length > 1 && (
          <div className="flex items-center justify-center gap-5 mt-1">
            <Button variant="ghost" size="icon" className="h-11 w-11" aria-label="Previous product image" onClick={prev}><ChevronLeft /></Button>
            <span aria-live="polite" className="min-w-12 text-center text-[12px] text-muted-foreground">
              {selectedImage + 1} / {images.length}
            </span>
            <Button variant="ghost" size="icon" className="h-11 w-11" aria-label="Next product image" onClick={next}><ChevronRight /></Button>
          </div>
        )}
      </div>

      {/* Fullscreen Lightbox */}
      <DialogPrimitive.Root open={lightboxOpen} onOpenChange={setLightboxOpen}>
      <AnimatePresence>
        {lightboxOpen && (
          <DialogPrimitive.Portal forceMount>
          <DialogPrimitive.Content asChild aria-describedby={undefined}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-foreground text-background flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
            onKeyDown={handleKeyDown}
            tabIndex={0}
          >
            <DialogPrimitive.Title className="sr-only">{title} — product images</DialogPrimitive.Title>
            <Button variant="ghost" size="icon"
              aria-label="Close image viewer"
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-background/10 hover:bg-background/20 hover:text-background"
            >
              <X className="h-5 w-5" />
            </Button>

            {images.length > 1 && (
              <>
                <Button variant="ghost" size="icon" aria-label="Previous image" onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full border border-background/30 bg-foreground/80 text-background hover:bg-foreground hover:text-background">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button variant="ghost" size="icon" aria-label="Next image" onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full border border-background/30 bg-foreground/80 text-background hover:bg-foreground hover:text-background">
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </>
            )}

            <motion.img
              key={selectedImage}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              src={images[selectedImage]?.node.url}
              alt={productImageAlt(title, { displayName: title, index: selectedImage, total: images.length })}
              className="max-w-[90vw] max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            <div className="absolute bottom-6 inset-x-4 flex justify-center flex-wrap">
              {images.map((_, i) => (
                <Button variant="ghost" size="icon"
                  key={i}
                  aria-label={`Go to image ${i + 1} of ${images.length}`}
                  onClick={(e) => { e.stopPropagation(); setSelectedImage(i); }}
                  aria-pressed={i === selectedImage}
                  className="w-11 h-11 rounded-full hover:bg-background/10 hover:text-background"
                ><span className={`w-2 h-2 rounded-full ${i === selectedImage ? "bg-background" : "bg-background/40"}`} /></Button>
              ))}
            </div>
          </motion.div>
          </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
      </DialogPrimitive.Root>
    </div>
  );
};
